import type { CampaignMetadata, LeadCardData } from '../types';
import { getNormalizedStatusKey, STATUS_MAP, type StatusType } from './statusMapping';
import { CREDIT_COST, calculateWeeksRemaining } from './creditPricing';

const ENRICHED_LEAD_STATUS_KEYS: StatusType[] = [
  'enriched',
  'outreaching',
  'outreached',
  'engaged',
  'proposal',
  'converted',
];

const toCount = (value: number | null | undefined): number => {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

export const getCampaignDiscardedLeadCount = (campaign: CampaignMetadata | null | undefined): number => {
  return toCount(campaign?.leads_by_status?.discarded);
};

export const getCampaignVisibleLeadCount = (campaign: CampaignMetadata | null | undefined): number => {
  if (!campaign) return 0;

  const targetCount = toCount(campaign.leads_to_target);
  const discardedCount = getCampaignDiscardedLeadCount(campaign);

  if (targetCount > 0) {
    return Math.max(targetCount - discardedCount, 0);
  }

  // Sum via the SHARED aggregate normalizer so raw n8n keys like `message_ready`
  // / `writting_message` (which normalize to OUTREACHING) count as visible.
  return getStatusCountsFromAggregate(campaign.leads_by_status).total_leads;
};

export const getCampaignEnrichedLeadCount = (campaign: CampaignMetadata | null | undefined): number => {
  if (!campaign?.leads_by_status) return 0;

  // Cumulative enriched via the SHARED aggregate normalizer (enriched + every
  // downstream sales status), consistent with the list card + summary cards.
  return getStatusCountsFromAggregate(campaign.leads_by_status).enriched_cumulative;
};

export const isDiscardedLead = (lead: Pick<LeadCardData, 'status'>): boolean => {
  return getNormalizedStatusKey(lead.status) === 'discarded';
};

export const isEnrichedLead = (lead: Pick<LeadCardData, 'status'>): boolean => {
  return ENRICHED_LEAD_STATUS_KEYS.includes(getNormalizedStatusKey(lead.status));
};

export const getVisibleLeads = <T extends LeadCardData>(leads: T[]): T[] => {
  return leads.filter((lead) => !(lead as any).isPlaceholder && !isDiscardedLead(lead));
};

export const getEnrichedLeads = <T extends LeadCardData>(leads: T[]): T[] => {
  return leads.filter((lead) => !(lead as any).isPlaceholder && isEnrichedLead(lead));
};

export const getLeadCompanyKey = (lead: Pick<LeadCardData, 'companyName'>): string => {
  return lead.companyName || 'Unknown Company';
};

const groupLeadsByCompany = <T extends LeadCardData>(leads: T[]): Map<string, T[]> => {
  const groups = new Map<string, T[]>();

  leads
    .filter((lead) => !(lead as any).isPlaceholder)
    .forEach((lead) => {
      const companyKey = getLeadCompanyKey(lead);
      const companyLeads = groups.get(companyKey) || [];
      companyLeads.push(lead);
      groups.set(companyKey, companyLeads);
    });

  return groups;
};

export const getActiveCompanyCount = (leads: LeadCardData[]): number => {
  return Array.from(groupLeadsByCompany(leads).values()).filter((companyLeads) => {
    return companyLeads.some((lead) => !isDiscardedLead(lead));
  }).length;
};

export const getDiscardedCompanyCount = (leads: LeadCardData[]): number => {
  return Array.from(groupLeadsByCompany(leads).values()).filter((companyLeads) => {
    return companyLeads.length > 0 && companyLeads.every(isDiscardedLead);
  }).length;
};

export const getCompanyCountForLeadStatus = (leads: LeadCardData[], statusKey: StatusType): number => {
  if (statusKey === 'discarded') {
    return getDiscardedCompanyCount(leads);
  }

  return new Set(
    leads
      .filter((lead) => !(lead as any).isPlaceholder && getNormalizedStatusKey(lead.status) === statusKey)
      .map(getLeadCompanyKey)
  ).size;
};

/**
 * Distinct-company count across MULTIPLE status keys (union). Used by summary
 * cards that represent a cumulative funnel slice — e.g. the ENRICHED card in
 * LEADS INTELLIGENCE counts every lead from enriched → converted, so its
 * company count must union companies across all of those statuses.
 *
 * The discarded case keeps the existing "company is discarded only when ALL its
 * leads are discarded" rule (getDiscardedCompanyCount) when discarded is the
 * sole requested key; otherwise discarded just contributes to the union.
 */
export const getCompanyCountForLeadStatuses = (leads: LeadCardData[], statusKeys: StatusType[]): number => {
  if (statusKeys.length === 1 && statusKeys[0] === 'discarded') {
    return getDiscardedCompanyCount(leads);
  }

  const keySet = new Set(statusKeys);

  return new Set(
    leads
      .filter((lead) => !(lead as any).isPlaceholder && keySet.has(getNormalizedStatusKey(lead.status)))
      .map(getLeadCompanyKey)
  ).size;
};

// ============================================================================
// SHARED TOTAL WEEKLY LIMIT — one inclusion rule + one sum for the Team-view
// "Total weekly limit" counter, so its number can never drift from the rule.
// ============================================================================

/**
 * Campaign statuses EXCLUDED from the TOTAL WEEKLY LIMIT. Deliberately a
 * deny-list (Manuel, 2026-09-13): the total is the team's LIVE outreach
 * capacity, so it counts ACTIVE plus every outreach-flavored status
 * ("outreaching", "outreached") and every transitional dot-status
 * ("enriching...", "writing message..."). Only non-consuming campaigns are
 * excluded: PAUSED/STOPPED (not spending), BLOCKED (halted), and terminal
 * COMPLETED/DONE/ARCHIVED (stopped spending). Empty/unknown status reads as
 * active (include).
 */
const WEEKLY_LIMIT_EXCLUDED_STATUSES: ReadonlySet<string> = new Set([
  'paused',
  'stopped',
  'blocked',
  'completed',
  'done',
  'archived',
  'archive',
]);

export const countsTowardWeeklyLimit = (status: string | null | undefined): boolean => {
  const normalized = String(status || '').trim().toLowerCase();
  return !WEEKLY_LIMIT_EXCLUDED_STATUSES.has(normalized);
};

/**
 * Sum of weekly_limit over every campaign that counts toward the team's TOTAL
 * WEEKLY LIMIT (see countsTowardWeeklyLimit). Single source of truth for the
 * TeamCampaignOverview counter.
 */
export const getTotalWeeklyLimit = (campaigns: CampaignMetadata[]): number => {
  return campaigns
    .filter((campaign) => countsTowardWeeklyLimit(campaign.status))
    .reduce((sum, campaign) => sum + (Number(campaign.weekly_limit) || 0), 0);
};

/** Result of getTeamWeeklyCreditRunway — how long the team's credits last. */
export interface TeamWeeklyRunway {
  /** TOTAL WEEKLY LIMIT (leads/week) over live campaigns. */
  weeklyLimit: number;
  /**
   * Estimated credits burned per week: every counted lead costs at least the
   * ENRICH credit (enrichment always applies). The conservative floor — the
   * team can ask "do our credits cover the weekly enrich?" from this number.
   */
  weeklyBurn: number | null;
  /** Floor(availableCredits / weeklyBurn) — null when burn is 0/unknown. */
  weeksRemaining: number | null;
  /** Credits SHORT of covering one full week of enrich (0 when covered). */
  creditsMissing: number;
  /** Coverage verdict: 'ok' when the balance covers THIS week's enrichment,
   *  'risk' when it doesn't (creditsMissing > 0), 'empty' at zero credits. */
  tone: 'ok' | 'risk' | 'empty';
}

/**
 * One-call weekly runway for the Team header: TOTAL WEEKLY LIMIT × ENRICH
 * credit vs the team's credit balance. Burn uses the ENRICH floor (not
 * per-campaign outreach costs) so the enrichment guarantee is what the chip
 * communicates; richer per-campaign burn stays in CampaignBurnRate.
 */
export const getTeamWeeklyCreditRunway = (
  campaigns: CampaignMetadata[],
  availableCredits: number | null | undefined
): TeamWeeklyRunway => {
  const weeklyLimit = getTotalWeeklyLimit(campaigns);
  const credits = Number(availableCredits) || 0;

  if (weeklyLimit <= 0) {
    return { weeklyLimit: 0, weeklyBurn: null, weeksRemaining: null, creditsMissing: 0, tone: credits > 0 ? 'ok' : 'empty' };
  }

  const weeklyBurn = weeklyLimit * CREDIT_COST.ENRICH;
  const weeksRemaining = calculateWeeksRemaining({ availableCredits: credits, weeklyBurn });
  const creditsMissing = Math.max(weeklyBurn - credits, 0);

  // COVERAGE verdict (Manuel, 2026-09-15): the pill answers ONE question —
  // "can we pay for this week's enrichment?" Credits cover the ENRICH floor →
  // green, whatever the forward runway; they don't → red with the exact
  // deficit. The old multi-week projection (green ≥8w / amber ≥4w / red <4w)
  // flagged healthy teams red just because their runway was short.
  const tone: TeamWeeklyRunway['tone'] =
    credits <= 0 ? 'empty'
      : creditsMissing === 0 ? 'ok'
        : 'risk';

  return { weeklyLimit, weeklyBurn, weeksRemaining, creditsMissing, tone };
};

// ============================================================================
// SHARED campaign status counting — single source of truth for both the
// campaign LIST card (Pipeline Flow) and the DETAIL view (summary cards + funnel).
// Both views call getCampaignStatusCounts so their numbers can never diverge.
// ============================================================================

/**
 * Normalized per-status breakdown used by every campaign status display. Same
 * shape whether the source is a lead array (detail view, most accurate) or a
 * `leads_by_status` aggregate (list view, from the campaign_metrics n8n action).
 *
 * Two enriched figures are exposed so each view can show what it means:
 *  - `enriched`         : leads whose CURRENT status is enriched (raw bucket).
 *  - `enriched_cumulative`: every lead from enriched → converted. Used by the
 *                          LEADS INTELLIGENCE summary card (a lead past enriched
 *                          is still enriched). The Pipeline Flow bar uses `enriched`.
 * `total_leads` excludes discarded.
 */
export interface CampaignStatusCounts {
  new_lead: number;
  enriching: number;
  enriched: number; // raw bucket — leads currently in enriched
  enriched_cumulative: number; // enriched + outreaching + outreached + engaged + proposal + converted
  outreaching: number;
  outreached: number;
  engaged: number;
  proposal: number;
  converted: number;
  discarded: number;
  total_leads: number; // active leads (everything except discarded)
}

// Statuses that count toward the cumulative ENRICHED bucket (enriched + downstream).
const ENRICHED_CUMULATIVE_KEYS: StatusType[] = ['enriched', 'outreaching', 'outreached', 'engaged', 'proposal', 'converted'];

// Active (in-funnel) statuses — everything except discarded.
const ACTIVE_STATUS_KEYS_FOR_TOTAL: StatusType[] = [
  'new_lead',
  'enriching',
  'enriched',
  'outreaching',
  'outreached',
  'engaged',
  'proposal',
  'converted',
];

const emptyCounts = (): CampaignStatusCounts => ({
  new_lead: 0,
  enriching: 0,
  enriched: 0,
  enriched_cumulative: 0,
  outreaching: 0,
  outreached: 0,
  engaged: 0,
  proposal: 0,
  converted: 0,
  discarded: 0,
  total_leads: 0,
});

/**
 * Count leads into the normalized breakdown from a LEAD ARRAY (detail view path).
 * Uses getNormalizedStatusKey so ENRICHED transitional substatuses ("Writing
 * message…", "Ready to Outreach", "Queued message…") correctly bucket as ENRICHED
 * (they moved from OUTREACHING), and applies the cumulative-ENRICHED rule.
 */
export const getStatusCountsFromLeads = (leads: LeadCardData[] | null | undefined): CampaignStatusCounts => {
  const counts = emptyCounts();

  if (!leads?.length) return counts;

  for (const lead of leads) {
    if ((lead as any).isPlaceholder) continue;
    const key = getNormalizedStatusKey(lead.status);
    if (key in counts) {
      (counts as Record<StatusType, number>)[key]++;
    }
  }

  // Collapse the cumulative buckets the same way the aggregate path does.
  return finalizeCounts(counts);
};

/**
 * Count into the normalized breakdown from a `leads_by_status` AGGREGATE object
 * (list view path — data from the campaign_metrics n8n action). Same rules as the
 * lead-array path, so both views agree.
 *
 * IMPORTANT: n8n's aggregate keys are the RAW lead-status strings
 * (`message_ready`, `writting_message`, `enriching`, ...), NOT the normalized
 * umbrella keys. So we normalize each KEY through getNormalizedStatusKey before
 * adding its count — otherwise `message_ready`/`writting_message` leads (which
 * the detail view buckets as OUTREACHING) would never land in any aggregate
 * bucket and the list card would show 0 for OUTREACHING/ENRICHED.
 */
export const getStatusCountsFromAggregate = (
  leadsByStatus: CampaignMetadata['leads_by_status'] | null | undefined
): CampaignStatusCounts => {
  const counts = emptyCounts();

  if (!leadsByStatus) return counts;

  const tally = counts as Record<StatusType, number>;
  for (const [rawKey, value] of Object.entries(leadsByStatus)) {
    const normalizedKey = getNormalizedStatusKey(rawKey);
    if (normalizedKey in tally) {
      tally[normalizedKey] += toCount(value as number);
    }
  }

  return finalizeCounts(counts);
};

/**
 * Apply the shared display rules to raw per-status counts: compute the
 * cumulative ENRICHED figure (enriched + every downstream sales status — kept
 * separate so the Pipeline Flow bar can show the raw `enriched` bucket while the
 * LEADS INTELLIGENCE summary card shows the cumulative one) and set total_leads
 * to the active (non-discarded) total.
 */
const finalizeCounts = (
  raw: Omit<CampaignStatusCounts, 'enriched_cumulative' | 'total_leads'>
): CampaignStatusCounts => {
  const enrichedCumulative = ENRICHED_CUMULATIVE_KEYS.reduce(
    (sum, key) => sum + (raw as Record<StatusType, number>)[key],
    0
  );

  const totalLeads = ACTIVE_STATUS_KEYS_FOR_TOTAL.reduce(
    (sum, key) => sum + (raw as Record<StatusType, number>)[key],
    0
  );

  return {
    ...raw,
    enriched_cumulative: enrichedCumulative,
    total_leads: totalLeads,
  };
};

/**
 * Resolve the normalized status counts for a campaign. Prefers a LEAD ARRAY
 * (current, complete — used by the detail view when leads are loaded) and falls
 * back to the `leads_by_status` aggregate (used by the list view, from
 * campaign_metrics). BOTH paths apply the same rules via finalizeCounts, so the
 * list card's Pipeline Flow and the detail's summary cards always agree.
 */
export const getCampaignStatusCounts = (
  campaignLeads: LeadCardData[] | null | undefined,
  leadsByStatus: CampaignMetadata['leads_by_status'] | null | undefined
): CampaignStatusCounts => {
  const realLeads = campaignLeads?.filter((lead) => !(lead as any).isPlaceholder);
  if (realLeads && realLeads.length > 0) {
    return getStatusCountsFromLeads(realLeads);
  }
  return getStatusCountsFromAggregate(leadsByStatus);
};

// ============================================================================
// SHARED Pipeline Flow stage builder — ONE function every Pipeline Flow render
// (detail funnel + both list-card variants) calls, so they can never drift.
// ============================================================================

/**
 * A single funnel stage: status key, display label, value, and bar/funnel color.
 */
export interface PipelineFlowStage {
  key: StatusType;
  label: string;
  value: number;
  color: string;
}

// Forward funnel order — New → Enriched → Converted. Discarded is NOT a stage
// (disqualified leads surface elsewhere), so it's excluded from the graph.
const PIPELINE_FLOW_STATUS_KEYS: StatusType[] = [
  'new_lead',
  'enriching',
  'enriched',
  'outreaching',
  'outreached',
  'engaged',
  'proposal',
  'converted',
];

export const PIPELINE_FLOW_LABELS: Record<StatusType, string> = {
  new_lead: 'New',
  enriching: 'Enriching',
  enriched: 'Enriched',
  outreaching: 'Outreaching',
  outreached: 'Outreached',
  engaged: 'Engaged',
  proposal: 'Proposal',
  converted: 'Converted',
  discarded: 'Discarded',
};

/**
 * Build Pipeline Flow stages from the SHARED status counts. This is the ONLY
 * builder every Pipeline Flow view should call — guarantees identical values
 * (incl. the cumulative-ENRICHED rule: a lead past enriched is still enriched)
 * across the detail funnel and both list-card bar variants.
 *
 * ENRICHED uses `counts.enriched_cumulative` (enriched + every Sales Intelligence
 * status), so it always reflects "enriched and beyond" regardless of which view
 * feeds it.
 *
 * DISCARDED is excluded from the forward funnel ON PURPOSE, but a campaign that
 * has discarded leads must still carry the stage — consumers such as the
 * Campaign Overview report render it as a separate negative pill and drop
 * leftover discarded lead rows when it is absent. Withholding it whenever the
 * count is zero is impossible: every other stage's label is a TRANSLATION KEY
 * (`stage.<key>`), so a zero-valued stage is indistinguishable from a stage the
 * dictionary does not know, and dropping it would make `translationTexts`
 * probe-and-diff on every render. So an empty stage yields 0, and each view
 * applies its own zero rule (the report simply skips rendering it).
 */
export const buildPipelineFlowStagesFromCounts = (counts: CampaignStatusCounts): PipelineFlowStage[] => {
  const keys = counts.discarded > 0
    ? ([...PIPELINE_FLOW_STATUS_KEYS, 'discarded'] as StatusType[])
    : PIPELINE_FLOW_STATUS_KEYS;
  return keys.map((statusKey) => ({
    key: statusKey,
    label: PIPELINE_FLOW_LABELS[statusKey],
    value: statusKey === 'enriched' ? counts.enriched_cumulative : counts[statusKey],
    color: STATUS_MAP[statusKey].color,
  }));
};

// ============================================================================
// SHARED funnel/bar REPRESENTATION scale — ONE visual curve for every Team
// overview chart (bars + horizontal funnel), so the two views always agree.
// ============================================================================

/**
 * Per-stage bar/band pixel heights for a pipeline chart. A VISUAL
 * representation, not a numerically exact scale:
 *   1. square-root perceptual curve — spreads mid/low stages apart so the
 *      silhouette reads as a funnel; the largest value spans the full track,
 *   2. readability floor — tiny values stay a legible band/pill.
 * CRITICALLY the curve is monotonic BY VALUE: a stage with more leads can
 * NEVER render smaller than one with fewer (the previous 95%-of-neighbor
 * taper clamp inverted real orderings — a 5 rendered above a 63). Bumps in
 * the silhouette are real data; the rendered numbers carry the exact values.
 */
export const buildFunnelThicknesses = (
  stages: Array<{ value: number }>,
  trackHeight: number,
  minThickness = 26
): number[] => {
  const live = stages.filter((stage) => stage.value > 0);
  if (live.length === 0) return [];
  const maxValue = Math.max(...live.map((stage) => stage.value));

  return stages.map((stage) => {
    if (stage.value <= 0) return 0;
    return Math.round(Math.max(minThickness, Math.sqrt(stage.value / maxValue) * trackHeight) * 100) / 100;
  });
};
